import { produce } from 'immer';
import { cloneDeep, get } from 'lodash';

import { Action } from '../../actions';
import {
    ADD_MEASURER_VALUE,
    ADD_SHOWS_COUNTERS_ROW,
    AddMeasurerValue,
    AddShowsCountersRowAction,
    CHANGE_MEASURER_VALUE,
    CHANGE_PIXEL_VALUE,
    CHANGE_SHOWS_COUNTERS_VALUE,
    CHANGE_TNS_ID_VALUE,
    ChangeMeasurerValue,
    ChangePixelValueAction,
    ChangeShowsCountersValueAction,
    ChangeTnsIdValueAction,
    DELETE_MEASURER,
    DeleteMeasurer,
    GET_BANNER_FOR_EDIT_MODERATION_REJECT_REASONS,
    GetBannnerForEditModerationRejectReasons,
    REMOVE_SHOWS_COUNTERS_ROW,
    RemoveShowsCountersRowAction,
    SET_BANNER_FOR_EDIT_MODERATION_REJECT_REASONS_ERROR,
    SetBannerForEditModerationRejectReasonsError,
    UPDATE_BANNER_FOR_EDIT_MODERATION_REJECT_REASONS,
    UpdateBannerForEditModerationRejectReasons
} from '../../actions/banners-edit/cpm-banner';
import { ADDITIONAL_SHOW_COUNTERS_LENGTH } from '../../constants';
import { isEditedBannerTypeCpmOutdoor } from '../../service/BannerHelpers/BannerHelpers';
import { cutErrors } from '../../service/cpm/cpm';
import {
    EditBannersState,
    getEditedBannerWithTnsId,
    getEditedCpmBanner,
    MassActionId
} from '../../typings/bannersEditState';
import { EditBannersInitialState } from '../bannersEdit';

const changePixelValue = (
    state: EditBannersState,
    action: ChangePixelValueAction
): EditBannersState => {
    if (action.bannerId === MassActionId.bannersEdit) {
        return { ...state };
    }

    const banner = getEditedCpmBanner(state.banners[action.bannerId]);
    if (!banner) {
        throw new Error('Unsupported banner type');
    }

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                pixel: action.value.trim(),
                errors: {
                    ...banner.errors,
                    pixels: cutErrors(
                        cloneDeep(get(banner, 'errors.pixels', {})),
                        action.errors
                    )
                }
            }
        }
    };
};

const changeMeasurerValue = (
    state: EditBannersState,
    action: ChangeMeasurerValue
): EditBannersState => {
    return produce(state, draft => {
        const banner = getEditedCpmBanner(draft.banners[action.bannerId]);

        if (!banner) {
            throw new Error('Unsupported banner type');
        }

        const measurers = banner.measurers;
        const typeIndex = measurers.findIndex(
            val => val.measurerSystem === action.value.measurerSystem
        );
        if (typeIndex === -1) {
            throw new Error(
                'trying to change measurer that is not in the state'
            );
        }

        measurers[typeIndex] = action.value;
        if (banner.errors && banner.errors.measurers) {
            banner.errors.measurers[typeIndex] = {};
        }
    });
};

const addMeasurerValue = (
    state: EditBannersState,
    action: AddMeasurerValue
): EditBannersState => {
    return produce(state, draft => {
        const banner = getEditedCpmBanner(draft.banners[action.bannerId]);

        if (!banner) {
            throw new Error('Unsupported banner type');
        }

        banner.measurers.push({ measurerSystem: action.value, params: '' });
    });
};

const deleteMeasurer = (
    state: EditBannersState,
    action: DeleteMeasurer
): EditBannersState => {
    return produce(state, draft => {
        const banner = getEditedCpmBanner(draft.banners[action.bannerId]);

        if (!banner) {
            throw new Error('Unsupported banner type');
        }

        const measurers = banner.measurers;
        const typeIndex = measurers.findIndex(
            val => val.measurerSystem === action.value
        );
        if (typeIndex === -1) {
            throw new Error(
                'trying to change measurer that is not in the state'
            );
        }

        measurers.splice(typeIndex, 1);
        // Удалили измеритель из массива -> удалить соответствующий ему индекс из объекта
        // ошибок, сместить другие ошибки на один индекс вниз
        if (banner.errors && banner.errors.measurers) {
            const oldMeasurers = banner.errors.measurers;
            banner.errors.measurers = Object.keys(oldMeasurers).reduce(
                (acc, key) => {
                    if (key === String(typeIndex)) {
                        return acc;
                    }

                    if (Number(key) > typeIndex) {
                        acc[Number(key) - 1] = oldMeasurers[key];
                    } else {
                        acc[Number(key)] = oldMeasurers[key];
                    }
                    return acc;
                },
                {}
            );
        }
    });
};

const changeTnsIdValue = (
    state: EditBannersState,
    action: ChangeTnsIdValueAction
): EditBannersState => {
    return produce(state, draft => {
        const banner = getEditedBannerWithTnsId(draft.banners[action.bannerId]);
        if (!banner) {
            throw new Error('Unsupported banner type');
        }

        banner.tnsId = action.value;

        if (banner.errors) {
            banner.errors.tnsId = {
                errors: []
            };
        }
    });
};

const changesShowsCountersValue = (
    state: EditBannersState,
    action: ChangeShowsCountersValueAction
): EditBannersState => {
    if (action.bannerId === MassActionId.bannersEdit) {
        return { ...state };
    }

    const banner = getEditedCpmBanner(state.banners[action.bannerId]);
    if (!banner) {
        throw new Error('Unsupported banner type');
    }

    const newShowsCounters = [...banner.showsCounters];
    newShowsCounters[action.index] = action.value;

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                showsCounters: newShowsCounters.map(count => count.trim()),
                errors: {
                    ...banner.errors,
                    pixels: cutErrors(
                        cloneDeep(get(banner, 'errors.pixels', {})),
                        action.errors
                    )
                }
            }
        }
    };
};

const removeShowsCounterRow = (
    state: EditBannersState,
    action: RemoveShowsCountersRowAction
): EditBannersState => {
    if (action.bannerId === MassActionId.bannersEdit) {
        return { ...state };
    }

    const banner = getEditedCpmBanner(state.banners[action.bannerId]);
    if (!banner) {
        throw new Error('Unsupported banner type');
    }

    const newShowsCounters = banner.showsCounters.filter((val, i) => {
        return i !== action.index;
    });

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                showsCounters: newShowsCounters,
                errors: {
                    ...banner.errors,
                    pixels: cutErrors(
                        cloneDeep(get(banner, 'errors.pixels', {})),
                        action.errors
                    )
                }
            }
        }
    };
};

const addShowsCountersRow = (
    state: EditBannersState,
    action: AddShowsCountersRowAction
): EditBannersState => {
    if (action.bannerId === MassActionId.bannersEdit) {
        return { ...state };
    }

    const banner = getEditedCpmBanner(state.banners[action.bannerId]);
    if (!banner) {
        throw new Error('Unsupported banner type');
    }
    if (banner.showsCounters.length > ADDITIONAL_SHOW_COUNTERS_LENGTH) {
        return state;
    }
    const newShowsCounters = [...banner.showsCounters];
    newShowsCounters.push('');

    return {
        ...state,
        banners: {
            ...state.banners,
            [action.bannerId]: {
                ...banner,
                showsCounters: newShowsCounters
            }
        }
    };
};

const getBannersModerationRejectReasonsReducer = (
    state: EditBannersState,
    action: GetBannnerForEditModerationRejectReasons
): EditBannersState => {
    const { bid: id } = action;
    const banner = state.banners[id];

    if (!isEditedBannerTypeCpmOutdoor(banner)) {
        return state;
    }

    const cpmOutdoorBanner = {
        ...banner,
        rejectReasons: undefined
    };

    return {
        ...state,
        banners: {
            ...state.banners,
            [id]: cpmOutdoorBanner
        }
    };
};

const updateBannerForEditModerationRejectReasonsReducer = (
    state: EditBannersState,
    action: UpdateBannerForEditModerationRejectReasons
): EditBannersState => {
    const { bid: id } = action;
    const banner = state.banners[id];

    if (!isEditedBannerTypeCpmOutdoor(banner)) {
        return state;
    }

    const cpmOutdoorBanner = {
        ...banner,
        rejectReasons: action.reasons
    };

    return {
        ...state,
        banners: {
            ...state.banners,
            [id]: cpmOutdoorBanner
        }
    };
};

const setBannerForEditModerationRejectErrorReasonsReducer = (
    state: EditBannersState,
    action: SetBannerForEditModerationRejectReasonsError
): EditBannersState => {
    const { bid: id } = action;
    const banner = state.banners[id];

    if (!isEditedBannerTypeCpmOutdoor(banner)) {
        return state;
    }

    const cpmOutdoorBanner = {
        ...banner,
        rejectReasons: {
            items: [],
            error: action.error
        }
    };

    return {
        ...state,
        banners: {
            [id]: cpmOutdoorBanner
        }
    };
};

export function cpmBannerReducer(
    state: EditBannersState = EditBannersInitialState,
    action: Action
): EditBannersState {
    switch (action.type) {
        case CHANGE_PIXEL_VALUE:
            return changePixelValue(state, action);
        case REMOVE_SHOWS_COUNTERS_ROW:
            return removeShowsCounterRow(state, action);
        case CHANGE_SHOWS_COUNTERS_VALUE:
            return changesShowsCountersValue(state, action);
        case ADD_SHOWS_COUNTERS_ROW:
            return addShowsCountersRow(state, action);
        case GET_BANNER_FOR_EDIT_MODERATION_REJECT_REASONS:
            return getBannersModerationRejectReasonsReducer(state, action);
        case UPDATE_BANNER_FOR_EDIT_MODERATION_REJECT_REASONS:
            return updateBannerForEditModerationRejectReasonsReducer(
                state,
                action
            );
        case SET_BANNER_FOR_EDIT_MODERATION_REJECT_REASONS_ERROR:
            return setBannerForEditModerationRejectErrorReasonsReducer(
                state,
                action
            );
        case CHANGE_MEASURER_VALUE:
            return changeMeasurerValue(state, action);
        case ADD_MEASURER_VALUE:
            return addMeasurerValue(state, action);
        case DELETE_MEASURER:
            return deleteMeasurer(state, action);
        case CHANGE_TNS_ID_VALUE:
            return changeTnsIdValue(state, action);
        default:
            return state;
    }
}
